import { createSlice } from '@reduxjs/toolkit';
import dotProp from 'dot-prop-immutable';
import { keyBy } from 'lodash';
import { FetchStatus } from 'data/core';
import { saveNormalizedData } from 'data/core/utils';
import { boardTaskSavedAction } from './actions';
import { taskNormalizer } from './schema';
const initialState = {
    items: {},
    itemsFetchStatus: {},
    isError: false,
    isLoading: false,
    lastBoardTaskSaved: null
};
const TasksSlice = createSlice({
    name: 'tasks',
    initialState,
    reducers: {
        saveTasks (state, { payload: { data, fetchStatus } }) {
            if (data.length === 0) {
                return state;
            }
            const mainTaskIds = data.map((d)=>d.taskId);
            const { entities: { tasks } } = taskNormalizer(data);
            const subTaskIds = Object.keys(tasks).filter((t)=>!mainTaskIds.includes(Number(t)));
            mainTaskIds.forEach((id)=>{
                state.items[id] = {
                    ...state.items[id],
                    ...tasks[id]
                };
                state.itemsFetchStatus[id] = state.itemsFetchStatus[id] === FetchStatus.Loaded ? FetchStatus.Loaded : fetchStatus;
            });
            subTaskIds.forEach((id)=>{
                state.items[id] = {
                    ...state.items[id],
                    ...tasks[id]
                };
                state.itemsFetchStatus[id] = state.itemsFetchStatus[id] === FetchStatus.Loaded ? FetchStatus.Loaded : FetchStatus.Partial;
            });
            return state;
        },
        saveNormalizedTask (state, action) {
            return saveNormalizedData(state, action);
        },
        saveNormalizedTasks (state, action) {
            return saveNormalizedData(state, action);
        },
        deleteTask (state, { payload }) {
            const [, , taskId] = payload;
            const task = dotProp.get(state.items, taskId);
            if (task) {
                let items = dotProp.delete(state.items, taskId);
                /**
         * Remove task reference from parent task object.
         */ const { parentTask } = task;
                if (parentTask) {
                    const parentTaskObject = dotProp.get(state.items, parentTask);
                    if (parentTaskObject != null && parentTaskObject.subTasks != null) {
                        items = dotProp.set(items, `${parentTask}`, {
                            ...parentTaskObject,
                            subTasks: parentTaskObject.subTasks.filter((_taskId)=>_taskId !== taskId)
                        });
                    }
                }
                return {
                    ...state,
                    items: {
                        ...items
                    }
                };
            }
            return state;
        },
        saveNormalizedPartialTasks (state, action) {
            const { payload } = action;
            if (payload && Object.keys(payload)) {
                // We do not want to change Fully Loaded task status to partial.
                Object.keys(payload).forEach((taskId)=>{
                    state.items[taskId] = {
                        ...state.items[taskId],
                        ...payload[taskId]
                    };
                    state.itemsFetchStatus[taskId] = state.itemsFetchStatus[taskId] === FetchStatus.Loaded ? FetchStatus.Loaded : FetchStatus.Partial;
                });
            }
            return state;
        },
        addSubTaskToTask (state, { payload }) {
            var _ref, _ref1;
            var _state_items;
            const { taskId, parentTask, taskName, fields } = payload;
            let parentTaskObj = (_state_items = state.items) === null || _state_items === void 0 ? void 0 : _state_items[parentTask];
            const parentSubTasks = (_ref = parentTaskObj === null || parentTaskObj === void 0 ? void 0 : parentTaskObj.subTasks) !== null && _ref !== void 0 ? _ref : [];
            const subTaskOrder = (_ref1 = parentTaskObj === null || parentTaskObj === void 0 ? void 0 : parentTaskObj.subTaskOrder) !== null && _ref1 !== void 0 ? _ref1 : [];
            if (parentTaskObj) {
                parentTaskObj = {
                    ...parentTaskObj,
                    subTaskOrder: [
                        ...subTaskOrder,
                        taskId
                    ],
                    subTasks: [
                        ...parentSubTasks,
                        taskId
                    ]
                };
                const newTask = {};
                newTask[taskId] = {
                    taskName,
                    taskId,
                    fields,
                    responsible: []
                };
                return {
                    ...state,
                    items: {
                        ...state.items,
                        ...newTask,
                        [parentTask]: {
                            ...parentTaskObj
                        }
                    }
                };
            }
            return state;
        },
        addMultipleSubTasksToTask (state, { payload }) {
            var _ref, _ref1;
            var _state_items;
            const { parentTask, fields, subtasks } = payload;
            let parentTaskObj = (_state_items = state.items) === null || _state_items === void 0 ? void 0 : _state_items[parentTask];
            const parentSubTasks = (_ref = parentTaskObj === null || parentTaskObj === void 0 ? void 0 : parentTaskObj.subTasks) !== null && _ref !== void 0 ? _ref : [];
            const subTaskOrder = (_ref1 = parentTaskObj === null || parentTaskObj === void 0 ? void 0 : parentTaskObj.subTaskOrder) !== null && _ref1 !== void 0 ? _ref1 : [];
            const taskIds = subtasks.map((e)=>e.taskId);
            if (parentTaskObj) {
                parentTaskObj = {
                    ...parentTaskObj,
                    subTaskOrder: [
                        ...subTaskOrder,
                        ...taskIds
                    ],
                    subTasks: [
                        ...parentSubTasks,
                        ...taskIds
                    ]
                };
                const newTask = {};
                subtasks.forEach((subtask)=>{
                    newTask[subtask.taskId] = {
                        taskName: subtask.taskName,
                        taskId: subtask.taskId,
                        fields
                    };
                });
                return {
                    ...state,
                    items: {
                        ...state.items,
                        ...newTask,
                        [parentTask]: {
                            ...parentTaskObj
                        }
                    }
                };
            }
            return state;
        },
        createSubTaskSuccess: (state, { payload })=>{
            var _ref;
            var _parentTaskObj_subTasks_filter, _parentTaskObj_subTasks;
            const { taskId, parentTask, task } = payload;
            const { entities: { tasks } } = taskNormalizer(task);
            const newState = dotProp.delete(state, `items.${taskId}`);
            let parentTaskObj = dotProp.get(newState.items, parentTask);
            const subTasks = (_ref = parentTaskObj === null || parentTaskObj === void 0 ? void 0 : (_parentTaskObj_subTasks = parentTaskObj.subTasks) === null || _parentTaskObj_subTasks === void 0 ? void 0 : (_parentTaskObj_subTasks_filter = _parentTaskObj_subTasks.filter) === null || _parentTaskObj_subTasks_filter === void 0 ? void 0 : _parentTaskObj_subTasks_filter.call(_parentTaskObj_subTasks, (id)=>id !== taskId)) !== null && _ref !== void 0 ? _ref : [];
            parentTaskObj = {
                ...parentTaskObj !== null && parentTaskObj !== void 0 ? parentTaskObj : {},
                subTasks: [
                    ...subTasks,
                    task.taskId
                ],
                subTaskOrder: [
                    ...subTasks,
                    task.taskId
                ]
            };
            const result = dotProp.set(newState, `items.${parentTask}`, parentTaskObj);
            return dotProp.merge(result, 'items', tasks);
        },
        createTaskSuccess: (state, { payload })=>{
            const { tempTaskId, task } = payload;
            const result = dotProp.delete(state, `items.${tempTaskId}`);
            const { entities: { tasks } } = taskNormalizer(task);
            return dotProp.merge(result, 'items', tasks);
        },
        createMultipleTaskSuccess: (state, { payload })=>{
            const { tasks: _tasks, subtasks } = payload;
            const result = subtasks.map((subtask)=>dotProp.delete(state, `items.${subtask.taskId}`));
            const { entities: { tasks } } = taskNormalizer(_tasks);
            return dotProp.merge(result, 'items', tasks);
        },
        fetchTaskDetailStart: (state, { payload })=>{
            return {
                ...state,
                itemsFetchStatus: {
                    ...state.itemsFetchStatus,
                    [payload]: FetchStatus.Loading
                }
            };
        },
        fetchTaskDetailError: (state, { payload })=>{
            return {
                ...state,
                itemsFetchStatus: {
                    ...state.itemsFetchStatus,
                    [payload]: FetchStatus.Error
                }
            };
        },
        updateTaskPrivacy: (state, { payload })=>{
            const { taskId, private: isPrivate } = payload;
            const task = state.items[taskId];
            return {
                ...state,
                items: {
                    ...state.items,
                    [taskId]: {
                        ...task,
                        private: isPrivate
                    }
                }
            };
        },
        updateTasksDates: (state, { payload })=>{
            const updatedTasks = {};
            payload.forEach(({ id, taskId, startDate, dueDate })=>{
                // const { id, taskId, startDate, dueDate } = data;
                const task = state.items[taskId || id];
                if (task) {
                    updatedTasks[taskId || id] = {
                        ...task,
                        startDate: startDate || task.startDate,
                        dueDate: dueDate || task.dueDate
                    };
                }
            });
            return {
                ...state,
                items: {
                    ...state.items,
                    ...updatedTasks
                }
            };
        },
        createDependencies: (state, { payload })=>{
            const updatedTasks = {};
            payload.forEach(({ data })=>{
                const { taskId, dependentTaskId } = data;
                const task = state.items[taskId];
                const dependentTask = state.items[dependentTaskId];
                const dependentTasks = (task === null || task === void 0 ? void 0 : task.dependentTasks) ? [
                    ...task.dependentTasks,
                    dependentTask
                ] : [
                    dependentTask
                ];
                updatedTasks[taskId] = {
                    ...task,
                    dependentTasks
                };
            });
            return {
                ...state,
                items: {
                    ...state.items,
                    ...updatedTasks
                }
            };
        },
        deleteDependencies: (state, { payload })=>{
            const updatedTasks = {};
            payload.forEach(({ data })=>{
                const { taskId, dependentTaskId } = data;
                const task = state.items[taskId];
                const dependentTasks = task === null || task === void 0 ? void 0 : task.dependentTasks.filter((_dTask)=>_dTask.taskId !== dependentTaskId);
                updatedTasks[taskId] = {
                    ...task,
                    dependentTasks
                };
            });
            return {
                ...state,
                items: {
                    ...state.items,
                    ...updatedTasks
                }
            };
        },
        removeProjectTasks: (state, { payload })=>{
            const tasks = Object.values(state.items).filter((task)=>!payload.includes(task.project));
            const items = keyBy(tasks, 'taskId');
            return {
                ...state,
                items
            };
        }
    },
    extraReducers: (builder)=>{
        builder.addCase(boardTaskSavedAction, (state, { payload })=>{
            state.lastBoardTaskSaved = {
                taskId: payload.taskId,
                projectId: payload.projectId
            };
        });
    }
});
export const { deleteTask, saveTasks, addSubTaskToTask, addMultipleSubTasksToTask, createSubTaskSuccess, createMultipleTaskSuccess, saveNormalizedTask, createTaskSuccess, fetchTaskDetailStart, fetchTaskDetailError, updateTaskPrivacy, saveNormalizedPartialTasks, updateTasksDates, createDependencies, deleteDependencies, removeProjectTasks } = TasksSlice.actions;
export const tasksReducer = TasksSlice.reducer;
